import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  calculatorService,
  CurrencyRate,
  ExplanationData,
  CalculatorData,
  ApiResponse,
  saveCalculationWithNotes,
  getJetSkiCalculation,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";

interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
}

interface SaveCalculationRequest {
  calculation_type: string;
  input_data: any;
  result_data: any;
  notes?: string;
}

// מפתחות השדות לטולטיפס הסבר
const STEP_KEYS = {
  manufacturing_date: "jetski_manufacturing_date",
  vehicle_price_foreign: "vehicle_price",
  currency_type: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost_foreign: "shipping_cost",
  insurance_cost_foreign: "insurance_cost",
  insurance_currency_type: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses_ils: "local_expenses",
  has_origin_declaration: "origin_declaration",
} as const;

const JetSkiCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
  });

  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [calculationResult, setCalculationResult] = useState<any>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);
  const [userNotes, setUserNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  // טעינת נתוני מחשבון
  useEffect(() => {
    loadCalculatorData();
  }, []);

  const loadCalculatorData = async () => {
    setIsLoading(true);
    try {
      const response: ApiResponse<CalculatorData> =
        await calculatorService.getCalculatorData();

      if (response.res === "OK" && response.data) {
        setCurrencies(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
      } else {
        throw new Error(response.msg || "שגיאה בטעינת נתוני המחשבון");
      }
    } catch (error) {
      console.error("שגיאה בטעינה:", error);
      setError("שגיאה בטעינת נתוני המחשבון");
    } finally {
      setIsLoading(false);
    }
  };

  // עדכון שער חליפין אוטומטי למטבע הרכב
  useEffect(() => {
    if (formData.currency_type && currencies.length > 0) {
      const selectedCurrency = currencies.find(
        (c) => c.currency_code === formData.currency_type
      );
      if (selectedCurrency) {
        // המרה לnumber כדי למנוע שגיאת toFixed
        const customsRate = Number(selectedCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          exchange_rate: customsRate.toFixed(4),
          insurance_currency_type: selectedCurrency.currency_code,
          insurance_exchange_rate: customsRate.toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencies]);

  // עדכון שער חליפין אוטומטי למטבע הביטוח
  useEffect(() => {
    if (formData.insurance_currency_type && currencies.length > 0) {
      const selectedInsuranceCurrency = currencies.find(
        (c) => c.currency_code === formData.insurance_currency_type
      );
      if (selectedInsuranceCurrency) {
        // המרה לnumber כדי למנוע שגיאת toFixed
        const insuranceCustomsRate =
          Number(selectedInsuranceCurrency.customs_rate) || 0;
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: insuranceCustomsRate.toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencies]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    const cleanValue = value.replace(/^0+/, "") || "0";
    handleInputChange(field, cleanValue);
  };

  const validateStep1 = () => {
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return false;
    }
    // בדיקת שדות חובה
    if (!formData.manufacturing_date) {
      setError("נא להזין תאריך ייצור");
      return false;
    }

    if (!formData.vehicle_price_foreign) {
      setError("נא להזין ערך אופנוע ים");
      return false;
    }

    if (!formData.currency_type) {
      setError("נא לבחור סוג מטבע");
      return false;
    }

    if (!formData.shipping_cost_foreign) {
      setError("נא להזין עלות הובלה");
      return false;
    }
    if (!formData.local_expenses_ils) {
      setError("נא להזין הוצאות מקומיות");
      return false;
    }

    return true;
  };

  const formatCurrency = (amount: number, currency: string = "ILS") => {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }

    if (currentStep === 1) {
      // בצע חישוב (ללא שמירה)
      performCalculation();
    }
  };

  const performCalculation = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getJetSkiCalculation({
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign) || 0,
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate) || 0,
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign) || 0,
        insurance_cost_foreign:
          parseFloat(formData.insurance_cost_foreign) || 0,
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate:
          parseFloat(formData.insurance_exchange_rate) || 0,
        local_expenses_ils: parseFloat(formData.local_expenses_ils) || 0,
        has_origin_declaration: formData.has_origin_declaration,
      });

      if (response.res === "OK" && response.data) {
        setCalculationResult(response.data);
        setCurrentStep(2);
      } else {
        setError(response.msg || "שגיאה בחישוב");
      }
    } catch (error) {
      console.error("שגיאה בחישוב:", error);
      setError("שגיאה בחישוב אופנוע הים");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCalculation = async () => {
    if (!calculationResult) return;

    setIsSaving(true);
    setError("");

    try {
      const saveRequest: SaveCalculationRequest = {
        calculation_type: "jet_ski_vehicle",
        input_data: calculationResult.input_data,
        result_data: calculationResult,
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => {
          navigate("/enduser/dashboard");
        }, 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("שגיאה בשמירה:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון מיסים לאופנוע ים",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  const handleGoBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/enduser/calculators/personal");
    }
  };

  const renderStep1 = () => (
    <MotionCard className="p-8">
      <h2 className="text-xl font-bold mb-6 text-center">
        חישוב מיסים לאופנוע ים
      </h2>
      <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="terms-calculator"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              setError("");
            }}
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="terms-calculator"
            className="text-sm text-gray-700 flex-1"
          >
            קראתי ואני מאשר/ת את{" "}
            <Link
              to="/terms-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline font-semibold"
            >
              תנאי השימוש ומדיניות הפרטיות
            </Link>{" "}
            *
          </label>
        </div>
      </div>
      {/* הצהרת מקור */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center mb-2">
          <label className="flex items-center font-medium text-blue-800">
            האם יש הצהרת מקור?
            <ExplanationTooltip
              stepKey={STEP_KEYS.has_origin_declaration}
              explanations={explanations}
            />
          </label>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="origin"
              checked={formData.has_origin_declaration}
              onChange={() => handleInputChange("has_origin_declaration", true)}
              className="ml-2"
            />
            יש הצהרת מקור
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="origin"
              checked={!formData.has_origin_declaration}
              onChange={() =>
                handleInputChange("has_origin_declaration", false)
              }
              className="ml-2"
            />
            אין הצהרת מקור
          </label>
        </div>
        <div className="mt-2 text-sm text-blue-600">
          💡 הצהרת מקור לא משפיעה על חישוב אופנועי ים (פטור ממכס)
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label
            htmlFor="manufacturing_date"
            className="flex items-center mb-2 font-medium"
          >
            תאריך ייצור האופנוע ים <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.manufacturing_date}
              explanations={explanations}
            />
          </label>
          <input
            id="manufacturing_date"
            type="date"
            value={formData.manufacturing_date}
            onChange={(e) =>
              handleInputChange("manufacturing_date", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            max={new Date().toISOString().split("T")[0]}
          />
        </div>

        <div>
          <label
            htmlFor="vehicle_price_foreign"
            className="flex items-center mb-2 font-medium"
          >
            ערך אופנוע הים במטבע המקור{" "}
            <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.vehicle_price_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="vehicle_price_foreign"
            type="number"
            value={formData.vehicle_price_foreign}
            onChange={(e) =>
              handleNumberInputChange("vehicle_price_foreign", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="currency_type"
            className="flex items-center mb-2 font-medium"
          >
            סוג מטבע <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="currency_type"
            value={formData.currency_type}
            onChange={(e) => handleInputChange("currency_type", e.target.value)}
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">בחר מטבע</option>
            {currencies.map((currency) => (
              <option
                key={currency.currency_code}
                value={currency.currency_code}
              >
                {currency.currency_name_hebrew} ({currency.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="exchange_rate"
            type="number"
            value={formData.exchange_rate}
            onChange={(e) =>
              handleNumberInputChange("exchange_rate", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.0000"
            min="0"
            step="0.0001"
          />
        </div>

        <div>
          <label
            htmlFor="shipping_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            עלות הובלה במטבע המקור <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.shipping_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="shipping_cost_foreign"
            type="number"
            value={formData.shipping_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("shipping_cost_foreign", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_cost_foreign"
            className="flex items-center mb-2 font-medium"
          >
            עלות ביטוח במטבע המקור
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_cost_foreign}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_cost_foreign"
            type="number"
            value={formData.insurance_cost_foreign}
            onChange={(e) =>
              handleNumberInputChange("insurance_cost_foreign", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>

        <div>
          <label
            htmlFor="insurance_currency_type"
            className="flex items-center mb-2 font-medium"
          >
            מטבע ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_currency_type}
              explanations={explanations}
            />
          </label>
          <select
            id="insurance_currency_type"
            value={formData.insurance_currency_type}
            onChange={(e) =>
              handleInputChange("insurance_currency_type", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">בחר מטבע ביטוח</option>
            {currencies.map((currency) => (
              <option
                key={currency.currency_code}
                value={currency.currency_code}
              >
                {currency.currency_name_hebrew} ({currency.currency_code})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="insurance_exchange_rate"
            className="flex items-center mb-2 font-medium"
          >
            שער חליפין ביטוח
            <ExplanationTooltip
              stepKey={STEP_KEYS.insurance_exchange_rate}
              explanations={explanations}
            />
          </label>
          <input
            id="insurance_exchange_rate"
            type="number"
            value={formData.insurance_exchange_rate}
            onChange={(e) =>
              handleNumberInputChange("insurance_exchange_rate", e.target.value)
            }
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0.0000"
            min="0"
            step="0.0001"
          />
        </div>

        <div>
          <label
            htmlFor="local_expenses_ils"
            className="flex items-center mb-2 font-medium"
          >
            הוצאות מקומיות בשקלים <span className="text-red-500 mr-1">*</span>
            <span className="sr-only">(שדה חובה)</span>
            <ExplanationTooltip
              stepKey={STEP_KEYS.local_expenses_ils}
              explanations={explanations}
            />
          </label>
          <input
            id="local_expenses_ils"
            type="number"
            value={formData.local_expenses_ils}
            onChange={(e) =>
              handleNumberInputChange("local_expenses_ils", e.target.value)
            }
            aria-required="true"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="0"
            min="0"
            step="0.01"
          />
        </div>
      </div>

      {/* מידע על אופנועי ים */}
      {/* <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
        <h4 className="font-medium text-green-800 mb-2">💡 מידע על אופנועי ים:</h4>
        <ul className="text-green-700 text-sm space-y-1">
          <li>• פטור ממכס לחלוטין</li>
          <li>• מס קניה: 20%</li>
          <li>• מע"מ: 18% על הסכום הכולל</li>
          <li>• הצהרת מקור לא משפיעה על החישוב</li>
        </ul>
      </div> */}

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      <div className="flex justify-between mt-8">
        <MotionButton
          onClick={handleGoBack}
          variant="secondary"
          className="px-6 py-3"
        >
          ← חזור
        </MotionButton>

        <MotionButton
          onClick={handleNextStep}
          disabled={isLoading}
          className="px-6 py-3"
        >
          {isLoading ? "מחשב..." : "חשב מיסים →"}
        </MotionButton>
      </div>
    </MotionCard>
  );

  const renderStep2 = () => {
    if (!calculationResult) return null;

    // שימוש במבנה הנכון מה-API
    const intermediate = calculationResult.intermediate_calculations;
    const finalResults = calculationResult.final_results;

    // חישוב סך המיסים ושיעור המיסים
    const totalTaxes =
      (intermediate?.purchase_tax || 0) + (finalResults?.vat || 0);
    const taxPercentage = (
      (totalTaxes / (intermediate?.purchase_tax_base || 1)) *
      100
    ).toFixed(1);
    // חיפוש מטבע לתצוגה
    const selectedCurrency = currencies.find(
      (c) => c.currency_code === formData.currency_type
    );

    return (
      <MotionCard className="p-8">
        <h3 className="text-xl font-bold mb-6 text-center">
          📊 תוצאות חישוב - אופנוע ים
        </h3>

        {/* אזור התוצאה הניתן להדפסה (לא כולל כרטיס ההערות/שמירה) */}
        <div ref={printRef} className="space-y-6">
        <TaxYearNotice />
        {/* קטע נתוני הקלט */}
        <div className="bg-blue-50 p-6 rounded-lg mb-6">
          <h4 className="font-bold text-lg mb-4 text-blue-800">
            נתוני הקלט שהוזנו:
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">תאריך ייצור:</span>
              <span className="font-medium">
                {new Date(formData.manufacturing_date).toLocaleDateString(
                  "he-IL"
                )}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">ערך אופנוע ים:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.vehicle_price_foreign || "0"
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">מטבע:</span>
              <span className="font-medium">
                {selectedCurrency?.currency_name_hebrew} (
                {formData.currency_type})
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">שער חליפין:</span>
              <span className="font-medium">
                {parseFloat(formData.exchange_rate || "0").toFixed(4)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות הובלה:</span>
              <span className="font-medium">
                {parseFloat(
                  formData.shipping_cost_foreign || "0"
                ).toLocaleString("he-IL")}{" "}
                {formData.currency_type}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">עלות ביטוח:</span>
              <span className="font-medium">
                {parseFloat(formData.insurance_cost_foreign || "0") > 0
                  ? `${parseFloat(
                      formData.insurance_cost_foreign || "0"
                    ).toLocaleString("he-IL")} ${
                      formData.insurance_currency_type
                    }`
                  : "ללא ביטוח"}
              </span>
            </div>

            {parseFloat(formData.insurance_cost_foreign || "0") > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">שער חליפין ביטוח:</span>
                <span className="font-medium">
                  {parseFloat(formData.insurance_exchange_rate || "0").toFixed(
                    4
                  )}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">הוצאות מקומיות:</span>
              <span className="font-medium">
                {parseFloat(formData.local_expenses_ils || "0") > 0
                  ? `₪${parseFloat(
                      formData.local_expenses_ils || "0"
                    ).toLocaleString("he-IL")}`
                  : "אין הוצאות מקומיות"}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-600">הצהרת מקור:</span>
              <span className="font-medium text-blue-600">
                {formData.has_origin_declaration ? "יש הצהרה ✓" : "אין הצהרה"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h4 className="font-bold text-lg mb-4">פירוט החישוב:</h4>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span>ערך כולל לצורכי מכס:</span>
              <span className="font-bold">
                {formatCurrency(intermediate?.total_value_for_customs || 0)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>סך המכס:</span>
              <span className="font-bold text-black">₪0 (פטור)</span>
            </div>

            <div className="flex justify-between">
              <span>שיעור המכס:</span>
              <span className="font-bold text-black">0%</span>
            </div>

            <div className="flex justify-between">
              <span>בסיס למס קניה:</span>
              <span className="font-bold">
                {formatCurrency(intermediate?.purchase_tax_base || 0)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>סך מס קניה:</span>
              <span className="font-bold text-black">
                {formatCurrency(intermediate?.purchase_tax || 0)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>שיעור מס קניה:</span>
              <span className="font-bold text-black">20%</span>
            </div>

            <div className="flex justify-between">
              <span>בסיס למע"מ:</span>
              <span className="font-bold">
                {formatCurrency(finalResults?.vat_base || 0)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>סך המע"מ:</span>
              <span className="font-bold text-black">
                {formatCurrency(finalResults?.vat || 0)}
              </span>
            </div>

            <div className="flex justify-between">
              <span>שיעור המע"מ:</span>
              <span className="font-bold text-black">18%</span>
            </div>

            <hr className="my-3" />

            <div className="flex justify-between">
              <span className="font-bold">סך המיסים:</span>
              <span className="font-bold text-black">
                {formatCurrency(totalTaxes)}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="font-bold">שיעור המיסים:</span>
              <span className="font-bold text-black">{taxPercentage}%</span>
            </div>

            <hr className="my-3" />

            <div className="flex justify-between text-lg">
              <span className="font-bold">עלות אופנוע הים כולל מיסים:</span>
              <span className="font-bold text-black">
                {formatCurrency(finalResults?.final_total || 0)}
              </span>
            </div>
          </div>
        </div>
        </div>
        {/* סוף אזור ההדפסה */}

        <div className="space-y-4 print-exclude">
          <div>
            <label className="block mb-2 font-medium">
              הערות (אופציונלי - עד 1000 תווים):
            </label>
            <textarea
              value={userNotes}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setUserNotes(e.target.value);
                }
              }}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={4}
              placeholder="הוסף הערות או הסברים נוספים לחישוב זה..."
            />
            <div className="text-sm text-gray-500 mt-1">
              {userNotes.length}/1000 תווים
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          {isSaved && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
              ✅ החישוב נשמר בהצלחה! מעביר לדף הבית...
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-8">
          <MotionButton
            onClick={handleGoBack}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            ← חזור לעריכה
          </MotionButton>

          <MotionButton
            onClick={handlePrint}
            variant="secondary"
            disabled={isSaving || isSaved}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200"
          >
            🖨️ הדפס
          </MotionButton>

          <MotionButton
            onClick={handleSaveCalculation}
            disabled={isSaving || isSaved}
            className="px-6 py-3"
          >
            {isSaving ? "שומר..." : isSaved ? "נשמר ✓" : "שמור וחזור לדף הבית"}
          </MotionButton>
        </div>
      </MotionCard>
    );
  };

  if (isLoading && !calculationResult) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>טוען נתוני מחשבון...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" dir="rtl">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון מיסים לאופנוע ים
          </h1>
          <p className="text-gray-600">
            חישוב מיסים לייבוא אופנוע ים עם מס קניה 20% (פטור ממכס)
          </p>
        </div>

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JetSkiCalculatorPage;
